import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import useFileUpload from '@/data/hook/shared/useFileUpload';
import {
  createBook,
  deleteBook,
  updateBook,
} from '@/data/services/bookServices';
import { booksQueryKeys } from '@/data/services/querykeys';
import Book, { bookType, MaterialType } from '@/models/Book';
import { getFileNameFromUrl } from '@/utils/file';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  ServiceUploadingOptions,
  UploadProgress,
} from '@/utils/shared/uploadProgress';
import { FileUploadStatus } from '@/components/shared/FileUpload/types';

export type RunInEditableLesson = <R>(
  action: (lessonId: number) => Promise<R>,
) => Promise<R>;

type UsePdfMaterialDraftParams = {
  bookType: bookType;
  book?: Book;
  runEditable: RunInEditableLesson;
};

export type UploadedPdf = {
  lessonId: number;
  book: Book;
};

export type PdfAttachment = {
  name: string;
  url: string;
  size?: number;
  isNew: boolean;
};

type SendPdfParams = {
  lessonId: number;
  file: File;
  options: ServiceUploadingOptions;
};

export type PdfMaterialDraft = {
  file?: File;
  status?: FileUploadStatus;
  progress: UploadProgress;
  uploaded?: UploadedPdf;
  attachment?: PdfAttachment;
  errorMessage?: string;
  isRemoving: boolean;
  start: (file: File) => void;
  cancel: () => void;
  retry: () => void;
  reset: () => void;
  discardDraft: () => Promise<void>;
  removeAttachment: () => Promise<void>;
};

const usePdfMaterialDraft = ({
  bookType,
  book: savedBook,
  runEditable,
}: UsePdfMaterialDraftParams): PdfMaterialDraft => {
  const queryClient = useQueryClient();
  const [savedFile, setSavedFile] = useState(savedBook?.file);

  const sendPdf = ({ lessonId, file, options }: SendPdfParams) => {
    if (savedBook)
      return updateBook(
        { lessonId, bookType, bookId: savedBook.id },
        { file },
        options,
      );
    return createBook(
      { lessonId, bookType },
      { filename: file.name, file, materialType: MaterialType.PDF_INTERACTIVE },
      options,
    );
  };

  const uploadPdf = (file: File, options: ServiceUploadingOptions) =>
    runEditable<UploadedPdf>(async editableLessonId => {
      const book = await sendPdf({
        lessonId: editableLessonId,
        file,
        options,
      });
      queryClient.setQueryData(booksQueryKeys.get(book.id).queryKey, book);
      return { lessonId: editableLessonId, book };
    });

  const {
    file,
    status,
    progress,
    result: uploaded,
    error,
    start,
    cancel,
    retry,
    reset,
  } = useFileUpload(uploadPdf);

  const { mutateAsync: removeBook, isPending: isRemoving } = useMutation({
    mutationFn: (bookId: number) =>
      runEditable(editableLessonId =>
        deleteBook({ lessonId: editableLessonId, bookType, bookId }),
      ),
  });

  const discardDraft = async () => {
    if (uploaded && !savedBook) await removeBook(uploaded.book.id);
  };

  const removeAttachment = async () => {
    await discardDraft();
    reset();
    setSavedFile(undefined);
  };

  const getAttachment = (): PdfAttachment | undefined => {
    const uploadedFile = uploaded?.book.file;
    if (status === 'uploaded' && file && uploadedFile)
      return {
        name: file.name,
        url: uploadedFile,
        size: file.size,
        isNew: true,
      };
    if (savedFile)
      return {
        name: getFileNameFromUrl(savedFile),
        url: savedFile,
        isNew: false,
      };
  };

  const errorMessage =
    isAxiosError(error) && error.response ? getErrorMessage(error) : undefined;

  return {
    file,
    status,
    progress,
    uploaded,
    attachment: getAttachment(),
    errorMessage,
    isRemoving,
    start,
    cancel,
    retry,
    reset,
    discardDraft,
    removeAttachment,
  };
};

export default usePdfMaterialDraft;
